import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ContentProvider } from './content/ContentProvider';
import { AuthProvider } from './context/AuthContext';
import AdminApp from './admin/AdminApp';
import Header from './components/home/Header';
import './App.css';
import ScrollToTop from './components/home/ScrollToTop';
import SiteBranding from './components/common/SiteBranding';
import RouteTransition from './components/common/RouteTransition';
import PrerenderReady from './components/common/PrerenderReady';
import StickyQuoteButton from './components/common/StickyQuoteButton';
import {
  markLeftHomeInSession,
  shouldDeferHeavyVisuals,
} from './utils/shouldSkipDoorIntro';
import { prefetchAboutUsRoute } from './utils/preloadTeamImages';
import {
  preloadSolutionVideos,
  prefetchSolutionNeedSection,
  releaseSolutionVideoWarmers,
} from './utils/preloadSolutionVideos';
import { prefetchHomeRoute } from './utils/preloadMedia';
import { prefetchBlogExperience, prefetchBlogDetailById } from './utils/preloadBlogMedia';
import { prefetchBlogDetailRoute } from './utils/preloadMedia';

const Home = lazy(() => import('./components/home/Home'));
const AboutUs = lazy(() => import('./components/about/AboutUs'));
const BlogDetail = lazy(() => import('./components/blog/BlogDetail'));
const CaseStudies = lazy(() => import('./components/case-studies/caseStudies'));
const CaseStudiesDetails = lazy(() => import('./components/case-studies/caseStudiesDetails'));
const InsightsInnovation = lazy(() => import('./components/blog-page/InsightsInnovation'));
const Services = lazy(() => import('./components/services/Services'));
const ServiceSlugRouter = lazy(() => import('./components/services/ServiceSlugRouter'));
const Chatbot = lazy(() => import('./components/Chatbot/Chatbot'));
const BookConsultation = lazy(() => import('./components/consultation/BookConsultation'));
const ContactUs = lazy(() => import('./components/contact/ContactUs'));
const ThankYou = lazy(() => import('./components/contact/ThankYou'));
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'));
const Terms = lazy(() => import('./components/legal/Terms'));
const NotFound = lazy(() => import('./components/common/NotFound'));
function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') {
      markLeftHomeInSession();
      releaseSolutionVideoWarmers();
    }
  }, [location.pathname]);

  /** Warm Home route chunk on homepage. */
  useEffect(() => {
    if (location.pathname !== '/') return undefined;
    prefetchHomeRoute();
    if (!shouldDeferHeavyVisuals()) {
      preloadSolutionVideos();
    }
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/blog') return undefined;
    prefetchBlogExperience();
    prefetchBlogDetailRoute();
    return undefined;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const match = location.pathname.match(/^\/(?:blog-detail|blog)\/([^/]+)/);
    if (!match) return undefined;
    const key = match[1];
    if (/^\d+$/.test(key)) {
      prefetchBlogDetailById(key);
      return undefined;
    }
    import('./components/blog/blogPostsData')
      .then((mod) => {
        const post = mod.BLOG_POSTS_BY_SLUG?.[key];
        if (post?.id != null) prefetchBlogDetailById(post.id);
      })
      .catch(() => {});
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return undefined;
    const run = () => {
      prefetchHomeRoute();
      prefetchSolutionNeedSection();
      if (shouldDeferHeavyVisuals()) return;
      preloadSolutionVideos();
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: shouldDeferHeavyVisuals() ? 15000 : 8000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(run, shouldDeferHeavyVisuals() ? 8000 : 4000);
    return () => window.clearTimeout(t);
  }, [location.pathname]);

  useEffect(() => {
    if (shouldDeferHeavyVisuals()) return undefined;
    const run = () => prefetchAboutUsRoute();
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 12000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(run, 6000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <>
      <div className="app-shell">
        <Header />
        <div className="App">
          <RouteTransition>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/blog" element={<InsightsInnovation />} />
            <Route path="/blog-page" element={<Navigate to="/blog" replace />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            <Route path="/blog-detail/:id" element={<BlogDetail />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudiesDetails />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceSlugRouter />} />
            <Route path="/book-consultation" element={<BookConsultation />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </RouteTransition>
        </div>
      </div>
    </>
  );
}

function MainSiteApp() {
  const [showChatbot, setShowChatbot] = useState(() => !shouldDeferHeavyVisuals());

  useEffect(() => {
    const handleCalendly = (e) => {
      if (e.origin === 'https://calendly.com' && e.data.event === 'calendly.event_scheduled') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'consultation_booked' });
      }
    };
    window.addEventListener('message', handleCalendly);
    return () => window.removeEventListener('message', handleCalendly);
  }, []);

  useEffect(() => {
    if (!shouldDeferHeavyVisuals()) {
      setShowChatbot(true);
      return undefined;
    }
    const reveal = () => setShowChatbot(true);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(reveal, { timeout: 20000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(reveal, 12000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const root = document.documentElement;

    const isTextInput = (el) =>
      el instanceof HTMLElement &&
      el.matches(
        'input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]):not([type="reset"]), textarea, [contenteditable="true"]'
      );

    const getKeyboardHeight = () => {
      if (!window.visualViewport) return 0;
      const viewport = window.visualViewport;
      return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    };

    const updateKeyboardOffset = () => {
      root.style.setProperty('--keyboard-offset', `${getKeyboardHeight()}px`);
    };

    /** Only scroll focused fields on small viewports or when the on-screen keyboard is open. */
    const shouldScrollFocusedInput = () => {
      if (getKeyboardHeight() > 80) return true;
      return window.innerWidth < 768;
    };

    const handleFocusIn = (event) => {
      if (!isTextInput(event.target)) return;
      document.body.classList.add('keyboard-open');
      updateKeyboardOffset();
      /** Chatbot uses its own visualViewport layout on mobile — avoid double-shift + scroll jump. */
      if (event.target instanceof HTMLElement && event.target.closest('.chatbot-window')) {
        return;
      }
      if (!shouldScrollFocusedInput()) return;
      setTimeout(() => {
        if (event.target instanceof HTMLElement && shouldScrollFocusedInput()) {
          if (event.target.closest('.chatbot-window')) return;
          event.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 120);
    };

    const handleFocusOut = () => {
      setTimeout(() => {
        if (!isTextInput(document.activeElement)) {
          document.body.classList.remove('keyboard-open');
          root.style.setProperty('--keyboard-offset', '0px');
        }
      }, 120);
    };

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    window.addEventListener('resize', updateKeyboardOffset);
    window.visualViewport?.addEventListener('resize', updateKeyboardOffset);
    window.visualViewport?.addEventListener('scroll', updateKeyboardOffset);

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('resize', updateKeyboardOffset);
      window.visualViewport?.removeEventListener('resize', updateKeyboardOffset);
      window.visualViewport?.removeEventListener('scroll', updateKeyboardOffset);
      root.style.setProperty('--keyboard-offset', '0px');
      document.body.classList.remove('keyboard-open');
    };
  }, []);

  return (
    <>
      <SiteBranding />
      <PrerenderReady />
      <ScrollToTop />
      <AppRoutes />
      <StickyQuoteButton />
      {showChatbot ? (
        <Suspense fallback={null}>
          <Chatbot />
        </Suspense>
      ) : null}
    </>
  );
}

function App() {
  return (
    <Router>
      <ContentProvider>
        <AuthProvider>
          <Routes>
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="*" element={<MainSiteApp />} />
          </Routes>
        </AuthProvider>
      </ContentProvider>
    </Router>
  );
}

export default App;
